import { useVerificationQuestionaries } from '../api/queries/useVerificationQuestionaries';
import { useVerificationQuestionariesSubmit } from '../api/queries/useVerificationQuestionariesSubmit';
import { useStepsController } from '@/shared/lib';
import { VerificationQuestion } from './VerificationQuestion';
import { Button } from '@/shared/components/ui/button';
import {
  Form,
  Loader,
  ModalComponentWrapper,
  ResultContent,
} from '@/shared/components';
import { useQuestionariesForm } from '../model';
import type { SubmitVerificationQuestionariesRequestDto } from '../api/types';
import { VerificationQuestionariesStart } from './VerificationQuestionariesStart';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '@/entities/session';

const VerificationQuestionariesForm = () => {
  const queryClient = useQueryClient();
  const { session } = useSessionStore();
  const { t } = useTranslation();
  const [isTestStarted, setIsTestStarted] = useState(false);

  const { questionaries, isQuestionariesLoading } =
    useVerificationQuestionaries();

  const {
    submitVerificationQuestionaries,
    isSubmitting,
    isSuccess: isTestPassed,
    isError: isTestFailed,
    resetQuestionariesSubmit,
  } = useVerificationQuestionariesSubmit();

  const {
    currentStepId,
    goBack,
    goForward,
    isFirstStep,
    isLastStep,
    setCurrentStepId,
  } = useStepsController(questionaries?.questions.length);

  const { form, setQuestionAnswer, resetQuestionaries } =
    useQuestionariesForm();

  const onSubmit = (data: SubmitVerificationQuestionariesRequestDto) => {
    submitVerificationQuestionaries(data);
  };

  const handleContinue = async () => {
    const currentQuestionField = `questions.${currentStepId}` as const;
    const isValid = await form.trigger(currentQuestionField);

    if (!isValid) {
      return;
    }

    if (isLastStep) {
      form.handleSubmit(onSubmit)();
    } else {
      goForward();
    }
  };

  const question = questionaries?.questions[currentStepId];
  const answers =
    form.watch(`questions.${currentStepId}.selectedAnswers`) || [];

  const currentQuestionError =
    form.formState.errors.questions?.[currentStepId]?.selectedAnswers;
  const hasValidationError = Boolean(currentQuestionError);

  if (currentStepId === 0 && !isTestStarted) {
    return (
      <ModalComponentWrapper title="">
        <VerificationQuestionariesStart
          startTest={() => setIsTestStarted(true)}
        />
      </ModalComponentWrapper>
    );
  }

  if (isLastStep && (isTestPassed || isTestFailed)) {
    const resultTitle = isTestPassed
      ? t('personalAccount.verification.questionariesResult.titleSuccess')
      : t('personalAccount.verification.questionariesResult.titleError');

    const resultDescription = isTestPassed
      ? t('personalAccount.verification.questionariesResult.descriptionSuccess')
      : t('personalAccount.verification.questionariesResult.descriptionError');

    const buttonText = isTestPassed
      ? t(
          'personalAccount.verification.questionariesResult.submitButtonSuccess'
        )
      : t('personalAccount.verification.questionariesResult.submitButtonError');

    const resultOnClick = () => {
      if (isTestPassed) {
        queryClient.invalidateQueries({
          queryKey: ['user-verification-info', session?.userId],
        });

        return;
      }

      resetQuestionariesSubmit();
      resetQuestionaries();
      setCurrentStepId(0);
      setIsTestStarted(false);
    };

    return (
      <ModalComponentWrapper title="">
        <ResultContent
          isSuccess={isTestPassed}
          title={resultTitle}
          description={resultDescription}
          buttonText={buttonText}
          onClick={resultOnClick}
        />
      </ModalComponentWrapper>
    );
  }

  return (
    <ModalComponentWrapper
      title={t('personalAccount.verification.questionaries.title', {
        index: currentStepId + 1,
        total: questionaries?.questions.length || 0,
      })}
      onBack={isFirstStep ? () => setIsTestStarted(false) : goBack}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {isQuestionariesLoading ? (
            <Loader />
          ) : (
            question && (
              <>
                <VerificationQuestion
                  key={question.questionId}
                  question={question}
                  setQuestionAnswer={setQuestionAnswer}
                  selectedAnswers={answers}
                  hasValidationError={hasValidationError}
                />
              </>
            )
          )}
          <Button
            type="button"
            onClick={handleContinue}
            size="lg"
            className="w-full mt-[5rem]"
            loading={isSubmitting}
            disabled={isSubmitting}>
            {t('personalAccount.verification.questionaries.submitButton')}
          </Button>
        </form>
      </Form>
    </ModalComponentWrapper>
  );
};

export { VerificationQuestionariesForm };
